import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import IconButton from '../../components/ui/IconButton';
import { useLoginMutation } from '../../hooks/mutations/useLoginMutation';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { LoginResponse } from '../../services/apiSLTP/auth';
import { capitalizeFirstLetter } from '../../utils/capitalize';

const Login: React.FC = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { login, isAuthenticated, authIsLoading } = useAuth();
    const { t } = useTranslation();
    const location = useLocation();
    const from = (location.state as { from?: string })?.from || '/';
    usePageTitle(capitalizeFirstLetter(t('login')));

    const { mutate, isSuccess, isError, isLoading, error, data } = useLoginMutation({
        onSuccess: (data: LoginResponse) => {
            login(username);
            window.location.replace(from);
        },
    });

    const handleUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setUsername(event.target.value);
    };

    const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage('');
        mutate({ username, password });
        setUsername('');
        setPassword('');
    };

    return (
        <div className="fx-c w-100 bg-black h-100 fx-justify-c fx-items-c left-0 top-0" style={{ position: 'absolute' }}>
            <motion.img
                initial={{ x: -300, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 1, ease: 'easeOut' }}
                src={require('../../assets/images/background/bg-welcome.png')}
                alt="welcome"
                style={{
                    width: '50%',
                    position: 'absolute',
                    left: 0,
                    bottom: 0,
                }}
            />

            <div className="fx-c fx-items-c border-radius-10px" style={{ width: '80%', zIndex: 1000 }}>
                <motion.img
                    initial={{ y: -500, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 1, ease: 'easeOut' }}
                    src={require('../../assets/images/logos/my-sltp-transp.png')}
                    alt="logo"
                    style={{
                        maxWidth: 300,
                        width: '100%',
                    }}
                />

                <form onSubmit={handleSubmit} style={{ paddingTop: 50 }} className="w-90 max-w-400px">
                    <div>
                        <TextField
                            required
                            fullWidth
                            id="username"
                            tabIndex={1}
                            label={capitalizeFirstLetter(t('username'))}
                            name="username"
                            autoFocus
                            value={username}
                            onChange={handleUsernameChange}
                            autoComplete="username"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderTopLeftRadius: '15px',
                                borderTopRightRadius: '15px',
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                },
                                '& .MuiFormLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'transparent',
                                        borderTopLeftRadius: '15px',
                                        borderTopRightRadius: '15px',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'white',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'white',
                                        borderWidth: '2px',
                                    },
                                },
                                '& .MuiFormLabel-root.Mui-focused': {
                                    color: 'white',
                                },
                            }}
                        />
                    </div>
                    <div className="fx-r fx-items-c fx-justify-sb" style={{ height: 5 }}>
                        <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', height: 5, width: '100%' }} className="fx-r fx-justify-fe">
                            <div style={{ backgroundColor: 'black', borderTopLeftRadius: 15, borderBottomLeftRadius: 15, width: '90%' }} />
                        </div>
                        <div className="bg-black pad-5px" style={{ borderRadius: 40, zIndex: 10, marginRight: -30 }}>
                            <div
                                style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: 30,
                                }}
                            >
                                <IconButton
                                    type="submit"
                                    iconName="login"
                                    loading={isLoading}
                                    tabIndex={3}
                                    size="large"
                                    iconProps={{ fontSize: 'large' }}
                                />
                            </div>
                        </div>
                    </div>
                    <div>
                        <TextField
                            required
                            fullWidth
                            name="password"
                            label={capitalizeFirstLetter(t('password'))}
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            tabIndex={2}
                            value={password}
                            onChange={handlePasswordChange}
                            autoComplete="current-password"
                            sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                borderBottomLeftRadius: '15px',
                                borderBottomRightRadius: '15px',
                                borderColor: 'rgba(255, 255, 255, 0.1)',
                                '& .MuiInputBase-input': {
                                    color: 'white',
                                    borderColor: 'rgba(255, 255, 255, 0.1)',
                                },
                                '& .MuiFormLabel-root': {
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    borderColor: 'rgba(255, 255, 255, 0.1)',
                                },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'transparent', // Définir une bordure initiale
                                        borderBottomLeftRadius: '15px',
                                        borderBottomRightRadius: '15px',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'white', // Bordure blanche au survol
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'white', // Bordure blanche au focus
                                        borderWidth: '2px', // Épaissir la bordure au focus
                                    },
                                },
                                '& .MuiFormLabel-root.Mui-focused': {
                                    color: 'white',
                                },
                            }}
                        />
                    </div>

                    {error && (
                        <Typography color="error" variant="body2" sx={{ marginTop: 2 }}>
                            {errorMessage}
                        </Typography>
                    )}
                </form>
            </div>
        </div>
    );
};

export default Login;
